import React, { useEffect, useMemo, useState } from 'react';
import { Card, List, Typography, Space, Select, App, Spin, Empty } from 'antd';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { apiClient } from '../services/api';
import { useSearchParams } from 'react-router-dom';
import { useMap } from 'react-leaflet';
import { useTranslation } from 'react-i18next';

const { Title, Text, Paragraph } = Typography;

interface GeoSummaryItem {
  country: string;
  code: string | null;
  count: number;
  lat: number | null;
  lng: number | null;
}

interface BrandItem {
  id: number;
  name: string;
  description: string;
  headquarters: string;
  website: string;
  categories: string[];
}

const defaultCenter: [number, number] = [20, 0];
const defaultZoom = 2;

const markerIcon = new L.Icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl:
    'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  shadowSize: [41, 41],
});

const WorldMapPage: React.FC = () => {
  const { message } = App.useApp();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<GeoSummaryItem[]>([]);
  const [activeCountry, setActiveCountry] = useState<string | undefined>();
  const [brands, setBrands] = useState<BrandItem[]>([]);
  const [viewCenter, setViewCenter] = useState<[number, number]>(defaultCenter);
  const [viewZoom, setViewZoom] = useState<number>(defaultZoom);

  const countriesWithCoords = useMemo(
    () => summary.filter(s => s.lat != null && s.lng != null),
    [summary]
  );

  const loadSummary = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get<{
        success: boolean;
        data: GeoSummaryItem[];
      }>('/geo/brands/summary');
      setSummary(res.data || []);
    } catch (err) {
      message.error(t('brands.loading_failed'));
    } finally {
      setLoading(false);
    }
  };

  const loadBrandsByCountry = async (opts: {
    code?: string;
    name?: string;
  }) => {
    try {
      const params = new URLSearchParams();
      if (opts.code) params.set('country', opts.code);
      if (opts.name) params.set('name', opts.name);
      const res = await apiClient.get<{ success: boolean; data: BrandItem[] }>(
        `/geo/brands?${params.toString()}`
      );
      setBrands(res.data || []);
    } catch (err) {
      message.error(t('brands.loading_failed'));
    }
  };

  useEffect(() => {
    loadSummary();
  }, []);

  const handleCountryChange = async (value?: string) => {
    setActiveCountry(value);
    setBrands([]);
    if (!value) return;
    const entry =
      summary.find(s => s.code === value) ||
      summary.find(s => s.country === value);
    if (entry) {
      await loadBrandsByCountry({
        code: entry.code || undefined,
        name: entry.country,
      });
      if (entry.lat != null && entry.lng != null) {
        setViewCenter([entry.lat, entry.lng]);
        setViewZoom(5);
      }
    }
  };

  // 地图视图控制器：在 center/zoom 变化时更新地图
  const MapController: React.FC<{ center: [number, number]; zoom: number }> = ({
    center,
    zoom,
  }) => {
    const map = useMap();
    useEffect(() => {
      map.setView(center, zoom, { animate: true });
    }, [center, zoom]);
    return null;
  };

  // 首次加载后，解析 URL 查询并预选定位
  useEffect(() => {
    // 1) 直接经纬度优先
    const qLat = searchParams.get('lat');
    const qLng = searchParams.get('lng');
    if (qLat && qLng) {
      const lat = parseFloat(qLat);
      const lng = parseFloat(qLng);
      if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
        setViewCenter([lat, lng]);
        setViewZoom(5);
        return;
      }
    }

    // 2) 其后按国家码/国家名
    if (summary.length === 0) return;
    const qCountry = searchParams.get('country') || undefined;
    const qName = searchParams.get('name') || undefined;
    if (!qCountry && !qName) return;
    const entry = qCountry
      ? summary.find(
          s => (s.code || '').toUpperCase() === qCountry.toUpperCase()
        )
      : summary.find(
          s => s.country.toLowerCase() === (qName || '').toLowerCase()
        );
    if (entry) {
      setActiveCountry(entry.code || entry.country);
      loadBrandsByCountry({
        code: entry.code || undefined,
        name: entry.country,
      });
      if (entry.lat != null && entry.lng != null) {
        setViewCenter([entry.lat, entry.lng]);
        setViewZoom(5);
      }
    }
  }, [summary, searchParams]);

  return (
    <div style={{ padding: 24 }}>
      <Title level={2} style={{ marginBottom: 16 }}>
        {t('worldmap.title')}
      </Title>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Card>
          <Space wrap>
            <Text type="secondary">{t('worldmap.by_country')}</Text>
            <Select
              allowClear
              placeholder={t('worldmap.select_country_placeholder')}
              style={{ minWidth: 200 }}
              options={summary
                .filter(s => !!s.code || !!s.country)
                .map(s => ({
                  label: `${s.country} (${s.count})`,
                  value: s.code || s.country,
                }))}
              value={activeCountry}
              onChange={handleCountryChange}
            />
            <Text type="secondary">
              {t('worldmap.summary_total', {
                total: summary.length,
                withCoords: countriesWithCoords.length,
              })}
            </Text>
          </Space>
        </Card>

        <Card>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 32 }}>
              <Spin />
            </div>
          ) : (
            <div style={{ height: 520, width: '100%' }}>
              <MapContainer
                center={viewCenter}
                zoom={viewZoom}
                style={{ height: '100%', width: '100%' }}
              >
                <MapController center={viewCenter} zoom={viewZoom} />
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {countriesWithCoords.map(c => (
                  <Marker
                    key={c.country}
                    position={[c.lat as number, c.lng as number]}
                    icon={markerIcon}
                    eventHandlers={{
                      click: async () => {
                        await handleCountryChange(c.code || c.country);
                      },
                    }}
                  >
                    <Popup>
                      <div>
                        <div style={{ fontWeight: 600 }}>{c.country}</div>
                        <div>
                          {t('worldmap.popup_brands_count', { count: c.count })}
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          )}
        </Card>

        <Card title={t('worldmap.country_brand_list')}>
          {activeCountry ? (
            brands.length > 0 ? (
              <List
                dataSource={brands}
                renderItem={b => (
                  <List.Item
                    key={b.id}
                    actions={[
                      <a key="view" href={`/brands/${b.id}`}>
                        {t('worldmap.view')}
                      </a>,
                    ]}
                  >
                    <List.Item.Meta
                      title={b.name}
                      description={
                        <div>
                          <Paragraph style={{ marginBottom: 8 }}>
                            {b.description}
                          </Paragraph>
                          <Text type="secondary">
                            {t('worldmap.headquarters')}：{b.headquarters}
                          </Text>
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            ) : (
              <Empty description={t('worldmap.empty_country')} />
            )
          ) : (
            <Empty description={t('worldmap.empty_hint')} />
          )}
        </Card>
      </Space>
    </div>
  );
};

export default WorldMapPage;
